import { useState } from 'react';
import { useUrlShortener } from '../../../url-sortener/hooks/useUrlShortener';
import type { TaskPayload } from '../../../../api';
import type { TaskDetails } from '../../../../services/scraper';
import { isSuccessDemoStatus } from '../utils';
import { useScrapingModals } from './useScrapingModals';
import { useScrapingForm } from './useScrapingForm';
import { useScrapingSelection } from './useScrapingSelection';
import { useScrapingData } from './useScrapingData';
import { useScrapingTasks } from './useScrapingTasks';
import { useScrapingCounts } from './useScrapingCounts';

export const useScraping = () => {
  const {
    isOpened,
    onOpen,
    onClose: onCloseModal,
    deleteTaskId,
    setDeleteTaskId,
    isDeleteModalOpen,
    handleDeleteClick,
    handleCloseDeleteModal,
    isBulkDeleteModalOpen,
    handleBulkDeleteClick,
    handleCloseBulkDeleteModal,
    selectedTask,
    isInfoModalOpen,
    handleInfoClick,
    handleCloseInfoModal,
    errorTask,
    isErrorModalOpen,
    handleErrorClick,
    handleCloseErrorModal,
    isDownloadModalOpen,
    onOpenDownloadModal,
    onCloseDownloadModal,
    downloadModalTaskId,
    setDownloadModalTaskId,
    isUploadScreenshotModalOpen,
    onOpenUploadScreenshotModal,
    onCloseUploadScreenshotModal,
    uploadScreenshotTaskId,
    setUploadScreenshotTaskId,
  } = useScrapingModals();

  const {
    formId,
    setFormId,
    formKey,
    formValues,
    setFormValues,
    bulkUploadData,
    handleBulkUpload,
    handleClearBulkUpload,
    resetFormState,
    taskData,
  } = useScrapingForm();

  const {
    selectedTaskIds,
    isSelectAllMode,
    handleSelectTask,
    handleSelectAll,
    clearSelection,
  } = useScrapingSelection();

  const {
    agentsOptions,
    campaignOptions,
    scrapingList,
    loadingScraping,
    reloadingScraping,
    reloading,
    refresh,
    filters,
    setFilters,
    setPage,
    pagination,
    statusMetrics,
    loadingStatusMetrics,
  } = useScrapingData();

  const [currentForm, setCurrentForm] = useState<
    Partial<TaskPayload> | undefined
  >(undefined);

  const { visitStats, isLoadingStats } = useUrlShortener(
    selectedTask?.demo_url || null,
    isInfoModalOpen
  );

  const handleClose = () => {
    onCloseModal();
    setCurrentForm(undefined);
    resetFormState();
  };

  const {
    handleAddScrapingTask,
    addingScraping,
    handleretryScrapingTask,
    retringScrapingTask,
    handleCancelScrapingTask,
    cancelingScrapingTask,
    handleDownloadScrapingTask,
    downloadingScraping,
    handleBulkScrapingTasksExport,
    bulkScrapingExport,
    handleDeleteScrapingTasks,
    deletingScraping,
    handleRefreshDemoTasks,
    refreshingDemo,
    handleConfirmDelete,
    handleConfirmBulkDelete,
    getExportableTaskIdsFromTasks,
    handleUploadScreenshot,
    uploadingScreenshot,
  } = useScrapingTasks({
    taskData,
    formKey,
    refresh,
    onClose: handleClose,
    resetFormState,
    clearSelection,
    scrapingList,
    selectedTaskIds,
    isSelectAllMode,
    filters,
    setDeleteTaskId,
    onCloseDeleteModal: handleCloseDeleteModal,
    onCloseBulkDeleteModal: handleCloseBulkDeleteModal,
  });

  const { exportableTaskCount, refreshableDemoTaskCount } = useScrapingCounts({
    scrapingList,
    selectedTaskIds,
    isSelectAllMode,
    getExportableTaskIdsFromTasks,
  });

  const handleRefreshDemoClick = () => {
    const processStringValue = (string: string): string | undefined => {
      if (typeof string !== 'string') return string;
      return string?.length ? string : undefined;
    };

    const campaignFilter =
      processStringValue(
        typeof filters?.campaign_name === 'string' ? filters.campaign_name : ''
      ) || null;

    const currentTasks = scrapingList?.[0]?.tasks || [];

    if (selectedTaskIds.length === 0 && !isSelectAllMode && campaignFilter) {
      handleRefreshDemoTasks(null, campaignFilter);
      return;
    }

    const tasksToCheck = isSelectAllMode
      ? currentTasks
      : currentTasks.filter((task: TaskDetails) =>
          selectedTaskIds.includes(task.id || '')
        );

    const refreshableTaskIds = tasksToCheck
      .filter((task: TaskDetails) => isSuccessDemoStatus(task.status))
      .map((task: TaskDetails) => task.id)
      .filter((id): id is string => Boolean(id));

    if (refreshableTaskIds.length > 0) {
      handleRefreshDemoTasks(
        refreshableTaskIds.length === currentTasks.length
          ? null
          : refreshableTaskIds,
        campaignFilter
      );
    }
  };

  return {
    currentForm,
    currentFormId: 'scraping',
    setCurrentForm,
    formId,
    setFormId,
    formKey,
    formValues,
    setFormValues,
    bulkUploadData,
    handleBulkUpload,
    handleClearBulkUpload,
    resetFormState,
    handleCancelScrapingTask,
    cancelingScrapingTask,
    handleAddScrapingTask,
    handleDownloadScrapingTask,
    handleretryScrapingTask,
    scrapingList,
    loading: loadingScraping || reloadingScraping || retringScrapingTask,
    updating: addingScraping,
    downloading: downloadingScraping,
    agentsOptions,
    campaignOptions,
    selectedTaskIds,
    handleSelectTask,
    handleSelectAll,
    clearSelection,
    handleBulkScrapingTasksExport,
    bulkScrapingExport,
    exportableTaskCount,
    handleDeleteScrapingTasks,
    deletingScraping,
    handleRefreshDemoTasks,
    refreshingDemo,
    refreshableDemoTaskCount,
    handleRefreshDemoClick,
    isOpened,
    onOpen,
    onClose: handleClose,
    refresh,
    filters,
    setFilters,
    deleteTaskId,
    isDeleteModalOpen,
    handleDeleteClick,
    handleConfirmDelete: () => handleConfirmDelete(deleteTaskId),
    handleCloseDeleteModal,
    isBulkDeleteModalOpen,
    handleBulkDeleteClick,
    handleConfirmBulkDelete,
    handleCloseBulkDeleteModal,
    selectedTask,
    isInfoModalOpen,
    handleInfoClick,
    handleCloseInfoModal,
    visitStats,
    isLoadingStats,
    errorTask,
    isErrorModalOpen,
    handleErrorClick,
    handleCloseErrorModal,
    isDownloadModalOpen,
    onOpenDownloadModal,
    onCloseDownloadModal,
    downloadModalTaskId,
    setDownloadModalTaskId,
    isUploadScreenshotModalOpen,
    onOpenUploadScreenshotModal,
    onCloseUploadScreenshotModal,
    uploadScreenshotTaskId,
    setUploadScreenshotTaskId,
    handleUploadScreenshot,
    uploadingScreenshot,
    setPage,
    pagination,
    reloading,
    statusMetrics,
    loadingStatusMetrics,
  };
};
